//#region Imports
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { CourseCategoryProxy } from 'src/app/models/proxies/course-category.proxy';
import { GetManyCouseCategoryProxy } from 'src/app/models/proxies/get-many-default-response-course-category.proxy';
import { CourseInteractor } from '../../interactors/course/course.interactor';
import { UserCertificateErrorsEnum } from '../../models/enums/user-certificate-error.enum';
import { DownloadCertificateReturnType } from '../../models/interfaces/download-certificate-return.type';
import { CreateCoursesWaitlistPayload } from '../../models/payloads/create-course-waitlist.payload';
import { RatingPayload } from '../../models/payloads/rating.payload';
import { CourseWaitlistProxy } from '../../models/proxies/course-waitlist.proxy';
import { CourseProxy } from '../../models/proxies/course.proxy';
import { GetManyCourseRatingsProxy } from '../../models/proxies/get-many-course-ratings.proxy';
import { SubscribedStudents } from '../../models/proxies/subscribed-stutends.proxy';
import { UserCertificateProxy } from '../../models/proxies/user-certificate.proxy';
import { getErrorMessage } from '../../utils/functions';
import { AuthService } from '../auth/auth.service';
import { EventTrackingService } from '../event-tracking/event-tracking.service';
import { GetManyCoursesProxy } from '../../models/proxies/get-many-courses.proxy';
import { LessonService } from '../lesson/lesson.service';
import { LessonDetailService } from '../../pages/main/lesson-detail-new/lesson-detail-content/lesson-detail.service';

//#endregion

@Injectable({
  providedIn: 'root',
})
export class CourseService {

  //#region Constructors

  constructor(
    private readonly courseInteractor: CourseInteractor,
    private readonly auth: AuthService,
    private readonly eventTracking: EventTrackingService,
    private readonly lessonService: LessonService,
    private readonly lessonDetailService: LessonDetailService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  //#endregion

  //#region Functions

  public async getAllCourses(
    categoryId: string, searchText: string, currentPage: number, limit: number
  ): Promise<GetManyCoursesProxy> {
    const { error, success } = await this.courseInteractor
      .getAllCourses(categoryId, searchText, currentPage, limit);

    if (error || !success)
      return {
        courses: undefined,
        errorMessage: getErrorMessage(error),
        hasMore: false
      };

    const courses = Array.isArray(success) ? success : success.data;

    return {
      courses,
      errorMessage: '',
      hasMore: courses.length >= limit
    };
  }

  public async getMyCourses(
    categoryId?: string, searchText?: string, currentPage?: number, limit?: number
  ): Promise<GetManyCoursesProxy> {
    const { error, success } = await this.courseInteractor
      .getMyCourses(categoryId, searchText, currentPage, limit);

    if (error || !success)
      return {
        courses: undefined,
        errorMessage: getErrorMessage(error),
        hasMore: false
      };

    const courses = Array.isArray(success) ? success : success.data;

    return {
      courses,
      errorMessage: '',
      hasMore: courses.length >= (limit || 0)
    };
  }

  public async getOtherCourses(
    categoryId: string, searchText: string, currentPage: number, limit: number
  ): Promise<GetManyCoursesProxy> {
    const { error, success } = await this.courseInteractor
      .getOtherCourses(categoryId, searchText, currentPage, limit);

    if (error || !success)
      return {
        courses: undefined,
        errorMessage: getErrorMessage(error),
        hasMore: false
      };

    const courses = Array.isArray(success) ? success : success.data;

    return {
      courses,
      errorMessage: '',
      hasMore: courses.length >= limit
    };
  }

  public async getCoursesWithoutCategory(currentPage: number, limit: number): Promise<[CourseProxy[] | null, string]> {
    const { error, success } = await this.courseInteractor.getCoursesWithoutCategory(currentPage, limit);

    if (error || !success)
      return [null, getErrorMessage(error)];

    const courses = Array.isArray(success) ? success : success.data;

    return [courses, ''];
  }

  public async getOneCourse(courseId: string, addJoin: boolean = true): Promise<[CourseProxy | null, string?]> {
    const { error, success } = await this.courseInteractor.getOneCourse(courseId, addJoin);

    if (error || !success)
      return [null, getErrorMessage(error)];

    if (success.modules)
      success.modules = success.modules.filter((module) => !!module.isActive);

    return [success];
  }

  public async getResumeLessonRoute(course: CourseProxy, courseIdentifier?: string): Promise<string[] | null> {
    const orderedLessons = this.getOrderedCourseLessons(course);

    if (orderedLessons.length === 0)
      return null;

    const lessonsById: Record<string, { moduleId: string; lessonId: string; watched: boolean }> = {};

    orderedLessons.forEach((lessonEntry) => {
      lessonsById[lessonEntry.lessonId] = lessonEntry;
    });

    const watchedLessonIds = new Set<string>(orderedLessons
      .filter((lessonEntry) => lessonEntry.watched)
      .map((lessonEntry) => lessonEntry.lessonId));

    const progressByLesson = await this.lessonService.getProgressBySubject(course.id);

    progressByLesson.forEach((progress) => {
      if (lessonsById[progress.lessonId as string])
        watchedLessonIds.add(progress.lessonId!);
    });

    const firstUnfinishedLesson = orderedLessons.find((lessonEntry) => !watchedLessonIds.has(lessonEntry.lessonId));

    const lastLessonEntry = course.lastLessonId
      ? lessonsById[course.lastLessonId]
      : null;

    const resumeLessonEntry = lastLessonEntry && !watchedLessonIds.has(lastLessonEntry.lessonId)
      ? lastLessonEntry
      : firstUnfinishedLesson || lastLessonEntry || orderedLessons[orderedLessons.length - 1];

    const targetCourseIdentifier = courseIdentifier || course.slug || course.id;

    await this.lessonDetailService.loadLesson(resumeLessonEntry.lessonId, course.id);

    return ['/main/cursos', targetCourseIdentifier, 'modulos', resumeLessonEntry.moduleId, 'aulas', resumeLessonEntry.lessonId];
  }

  public async getDownloadCertificateState(
    courseId: string
  ): Promise<[Blob | null, DownloadCertificateReturnType?]> {
    const user = this.auth.getCurrentUser();

    if (!user)
      return [
        null,
        { state: 'error', message: 'Você não está logado na plataforma.' }
      ];

    const { error, success } = await this.courseInteractor.downloadCertificate(user.id, courseId);

    if (error || !success) {
      const certificateErrorType: UserCertificateErrorsEnum = error?.error?.certificateErrorType || UserCertificateErrorsEnum.UNKNOWN;

      switch (certificateErrorType) {
        case UserCertificateErrorsEnum.PRESENCE_WATCH_REQUIRED:
          return [null, { state: 'presenceWatchRequired', message: getErrorMessage(error) }];

        case UserCertificateErrorsEnum.PRESENCE_TEAM_REQUIRED:
          return [null, { state: 'presenceTeamRequired', message: getErrorMessage(error) }];

        case UserCertificateErrorsEnum.QUIZ_REQUIRED:
          return [null, { state: 'quizRequired', message: getErrorMessage(error) }];

        default:
          return [null, { state: 'error', message: getErrorMessage(error) }];
      }
    }

    return [success];
  }

  public async downloadCertificate(courseId: string): Promise<DownloadCertificateReturnType> {
    const [blob, stateResult] = await this.getDownloadCertificateState(courseId);

    if (!blob) return stateResult as DownloadCertificateReturnType;

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = `Certificado ${courseId}.pdf`;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    return { state: 'downloaded' };
  }

  public async openCertificate(courseId: string): Promise<[boolean, DownloadCertificateReturnType?]> {
    const [blob, stateResult] = await this.getDownloadCertificateState(courseId);

    if (!blob)
      return [false, stateResult];

    const fileURL = URL.createObjectURL(blob);
    this.document.defaultView?.open(fileURL, '_blank', 'noopener');

    return [true];
  }

  public async getPurchasedCourses(): Promise<[CourseProxy[] | null, string?]> {
    const { error, success } = await this.courseInteractor.getPurchasedCourses();

    if (error || !success)
      return [null, getErrorMessage(error)];

    return [success];
  }

  public async getSubscriptionForCourse(courseId: string): Promise<[CourseWaitlistProxy | null, string?]> {
    const currentUser = this.auth.getCurrentUser();

    if (!currentUser)
      return [null, 'O usuário não está autenticado, logo, não é possível buscar a informação se ele está na lista de espera desse curso.'];

    const { error, success } = await this.courseInteractor.getSubscriptionByUserAndCourse(currentUser.id, courseId);

    if (error || !success)
      return [null, getErrorMessage(error)];

    const [subscription] = Array.isArray(success) ? success : success.data;

    if (!subscription)
      return [null];

    return [subscription];
  }

  public async createSubscriptionToCourse(payload: CreateCoursesWaitlistPayload): Promise<[CourseWaitlistProxy | null, string?]> {
    const { error, success } = await this.courseInteractor.createSubscriptionToCourse({
      userId: payload.userId,
      sessionUid: this.eventTracking.sessionUid,
      email: payload.email,
      name: payload.name,
      courseId: payload.courseId,
    });

    if (error || !success)
      return [null, getErrorMessage(error)];

    return [success];
  }

  public async exitCourseWaitlist(wailistId: string): Promise<[boolean, string]> {
    const { error } = await this.courseInteractor.deleteSubscriptionToCourse(wailistId);

    if (error)
      return [false, getErrorMessage(error)];

    return [true, 'Você saiu da lista de espera deste curso'];
  }

  public async getSubscribedCourseTeamIdByCourseId(courseId: string): Promise<[string | null, string?]> {
    const currentUser = this.auth.getCurrentUser();

    if (!currentUser)
      return [null, $localize`Você não está autenticado na plataforma, logo, não é possível visualizar as informações da aula.`];

    const { error, success } = await this.courseInteractor.getSubscribedCourseTeamByCourseIdAndUserId(currentUser.id, courseId);

    if (error || !success)
      return [null, getErrorMessage(error)];

    const subscribedTeams = Array.isArray(success) ? success : success.data;

    if (subscribedTeams.length === 0)
      return [null, $localize`Você não está inscrito em nenhuma turma desse curso, logo, não pode visualizar as informações da aula.`];

    const subscribedTeam = subscribedTeams[0];

    return [subscribedTeam.courseTeamId];
  }

  public async getUserCertificates(): Promise<[UserCertificateProxy[] | undefined, string?]> {
    const currentUser = this.auth.getCurrentUser();

    if (!currentUser)
      return [undefined, $localize`Você não está autenticado na plataforma, logo, não é possível visualizar as informações da aula.`];

    const { error, success } = await this.courseInteractor.getUserCertificates(currentUser.id);

    if (error || !success)
      return [undefined, getErrorMessage(error)];

    const certificate = Array.isArray(success) ? success : success.data;

    return [certificate];
  }

  public async getUserCertificatesByCourses(coursesIds: string[]): Promise<[UserCertificateProxy[] | undefined, string?]> {
    const currentUser = this.auth.getCurrentUser();

    if (!currentUser)
      return [undefined, $localize`Você não está autenticado na plataforma, logo, não é possível visualizar as informações da aula.`];

    const { error, success } = await this.courseInteractor.getUserCertificatesByCourses(currentUser.id, coursesIds);

    if (error || !success)
      return [undefined, getErrorMessage(error)];

    const certificate = Array.isArray(success) ? success : success.data;

    return [certificate];
  }

  public async getManyCertificates(): Promise<[UserCertificateProxy[] | undefined, string?]> {
    const currentUser = this.auth.getCurrentUser();

    if (!currentUser)
      return [undefined, $localize`Você não está autenticado na plataforma, logo, não é possível visualizar as informações da aula.`];

    const { error, success } = await this.courseInteractor.getUserCertificates(currentUser.id);

    if (error || !success)
      return [undefined, getErrorMessage(error)];

    const certificate = Array.isArray(success) ? success : success.data;

    return [certificate];
  }

  public async getUserCertificateByCourseId(courseId: string): Promise<[UserCertificateProxy | undefined, string?]> {
    const currentUser = this.auth.getCurrentUser();

    if (!currentUser)
      return [undefined, $localize`Você não está autenticado na plataforma, logo, não é possível visualizar as informações da aula.`];

    const { error, success } = await this.courseInteractor.getUserCertificateByCourseId(currentUser.id, courseId);

    if (error || !success)
      return [undefined, getErrorMessage(error)];

    return [success.data.pop()];
  }

  public async getRatingsFromCourse(courseId: string): Promise<[GetManyCourseRatingsProxy | undefined, string?]> {
    const { error, success } = await this.courseInteractor.getRatingsFromCourse(courseId);

    if (error)
      return [undefined, getErrorMessage(error)];

    return [success];
  }

  public async sendRating(ratingPayload: RatingPayload): Promise<[boolean, string?]> {
    const { error } = await this.courseInteractor.sendRating(ratingPayload);

    if (error)
      return [false, getErrorMessage(error)];

    return [true];
  }

  public async editRating(ratingPayload: RatingPayload): Promise<[boolean, string?]> {
    const { error } = await this.courseInteractor.editRating(ratingPayload);

    if (error)
      return [false, getErrorMessage(error)];

    return [true];
  }

  public async getSubscribedStudents(courseId: string): Promise<[SubscribedStudents?, string?]> {
    const { success, error } = await this.courseInteractor.getSubscribedStudents(courseId);

    if (error || !success)
      return [undefined, getErrorMessage(error)];

    return [success];
  }

  public async getCategoriesSortedByName(): Promise<[CourseCategoryProxy[], string?]> {
    const { success, error } = await this.courseInteractor.getCategoriesSortedByName();

    if (error || !success)
      return [[], getErrorMessage(error)];

    return [success.data.filter(category => category.courses.length > 0)];
  }

  public async getCoursesGroupedByCategories(
    page: number,
    limit: number,
    numberOfCourses: number,
    categoryId?: string | null,
    searchText?: string,
  ): Promise<[GetManyCouseCategoryProxy, string?]> {
    const { success, error } = await this.courseInteractor.getCoursesGroupedByCategories(page, limit, numberOfCourses, categoryId, searchText);

    if (error || !success)
      return [
        {
          count: 0,
          total: 0,
          page: 0,
          pageCount: 0,
          data: []
        },
        getErrorMessage(error)
      ];

    return [success];
  }

  private getOrderedCourseLessons(course: CourseProxy): { moduleId: string; lessonId: string; watched: boolean }[] {
    if (!Array.isArray(course?.modules))
      return [];

    return [...course.modules]
      .filter((subjectModule) => subjectModule?.isActive !== false)
      .sort((subjectModuleA, subjectModuleB) => (subjectModuleA.order || 0) - (subjectModuleB.order || 0))
      .reduce((lessonEntries, subjectModule) => {
        const orderedLessons = [...(subjectModule.lessons || [])]
          .filter((lesson) => !!lesson?.id)
          .sort((lessonA, lessonB) => (lessonA.order || 0) - (lessonB.order || 0));

        orderedLessons.forEach((lesson) => {
          lessonEntries.push({
            moduleId: subjectModule.id,
            lessonId: lesson.id,
            watched: !!lesson.watched,
          });
        });

        return lessonEntries;
      }, [] as { moduleId: string; lessonId: string; watched: boolean }[]);
  }

  //#endregion

}
